/*
 * File: rx-ng-zone-scheduler.ts
 * Original source: https://github.com/ftischler/ngx-rxjs-zone-scheduler
 *
 * This file has been copied from the 'ngx-rxjs-zone-scheduler' repository authored by Florian Tischler.
 * All credit for the original creation and subsequent updates goes to Florian Tischler.
 * Please refer to the original repository for the latest version of this file.
 *
 * This project's usage of this file adheres to the terms of the MIT License as specified in the original repository.
 *
 * MIT License
 *
 * Copyright (c) Florian Tischler
 *
 */

import { Injectable, NgZone, inject } from '@angular/core';
import { asyncScheduler, MonoTypeOperatorFunction, SchedulerLike, Subscription } from 'rxjs';
import { observeOn, subscribeOn } from 'rxjs/operators';

type Work<T> = (state?: T) => void;
type Delay = number | undefined;

abstract class ZoneScheduler implements SchedulerLike {
  constructor(
    protected ngZone: NgZone,
    protected scheduler: SchedulerLike,
  ) {}

  abstract schedule<T>(...args: [Work<T>, Delay, T]): Subscription;

  now(): number {
    return this.scheduler.now();
  }
}

class LeaveZoneScheduler extends ZoneScheduler {
  override schedule<T>(...args: [Work<T>, Delay, T]): Subscription {
    return this.ngZone.runOutsideAngular(() => {
      return this.scheduler.schedule(...args);
    });
  }
}

class EnterZoneScheduler extends ZoneScheduler {
  override schedule<T>(...args: [Work<T>, Delay, T]): Subscription {
    return NgZone.isInAngularZone()
      ? this.scheduler.schedule(...args)
      : this.ngZone.run(() => this.scheduler.schedule(...args));
  }
}

export function enterNgZone(ngZone: NgZone, scheduler: SchedulerLike = asyncScheduler): SchedulerLike {
  return new EnterZoneScheduler(ngZone, scheduler);
}

export function leaveNgZone(ngZone: NgZone, scheduler: SchedulerLike = asyncScheduler): SchedulerLike {
  return new LeaveZoneScheduler(ngZone, scheduler);
}

@Injectable({ providedIn: 'root' })
export class RxNgZoneScheduler {
  private ngZone = inject(NgZone);

  public observeOnNgZone<T>(scheduler?: SchedulerLike): MonoTypeOperatorFunction<T> {
    return observeOn<T>(this.enterNgZone(scheduler));
  }

  public observeOutOfNgZone<T>(scheduler?: SchedulerLike): MonoTypeOperatorFunction<T> {
    return observeOn<T>(this.leaveNgZone(scheduler));
  }

  public subscribeOnNgZone<T>(scheduler?: SchedulerLike): MonoTypeOperatorFunction<T> {
    return subscribeOn<T>(this.enterNgZone(scheduler));
  }

  public subscribeOutOfNgZone<T>(scheduler?: SchedulerLike): MonoTypeOperatorFunction<T> {
    return subscribeOn<T>(this.leaveNgZone(scheduler));
  }

  public enterNgZone(scheduler: SchedulerLike = asyncScheduler): SchedulerLike {
    return enterNgZone(this.ngZone, scheduler);
  }

  public leaveNgZone(scheduler: SchedulerLike = asyncScheduler): SchedulerLike {
    return leaveNgZone(this.ngZone, scheduler);
  }
}
